import { useNavigate } from 'react-router-dom';
import { useAuthStore, getAllRoles } from '@/stores/auth.store';
import {
  Shield, UserCog, BarChart2, Cog, Scale, Building2, CheckSquare,
} from 'lucide-react';

type KnownRole = 'SUPER_ADMIN' | 'ADMIN' | 'COMMERCIAL' | 'OPERATIONS' | 'JURISTE' | 'VALIDATEUR' | 'CLIENT';

const PROFILE_META: Record<KnownRole, {
  label: string; icon: React.ElementType; color: string; border: string; dashboard: string;
}> = {
  SUPER_ADMIN: { label: 'Super Admin', icon: Shield,      color: '#7c3aed', border: '#c4b5fd', dashboard: '/app/admin/dashboard' },
  ADMIN:       { label: 'Admin',       icon: UserCog,     color: '#B8960A', border: '#fbbf24', dashboard: '/app/admin/dashboard' },
  COMMERCIAL:  { label: 'Commercial',  icon: BarChart2,   color: '#ea580c', border: '#fb923c', dashboard: '/app/admin/dashboard' },
  OPERATIONS:  { label: 'Opérations', icon: Cog,         color: '#0891b2', border: '#38bdf8', dashboard: '/app/admin/dashboard' },
  JURISTE:     { label: 'Juriste',     icon: Scale,       color: '#0d9488', border: '#2dd4bf', dashboard: '/app/juriste/dashboard' },
  VALIDATEUR:  { label: 'Validateur',  icon: CheckSquare, color: '#7c3aed', border: '#c4b5fd', dashboard: '/app/validateur/dashboard' },
  CLIENT:      { label: 'Client',      icon: Building2,   color: '#16a34a', border: '#4ade80', dashboard: '/app/client/dashboard' },
};

interface ProfileSwitcherProps {
  theme?: 'light' | 'dark';
}

export default function ProfileSwitcher({ theme = 'dark' }: ProfileSwitcherProps) {
  const { user, activeRole, switchProfile } = useAuthStore();
  const navigate = useNavigate();

  const allRoles = getAllRoles(user);
  if (allRoles.length <= 1) return null;

  const handleSwitch = (role: KnownRole) => {
    if (role === activeRole) return;
    switchProfile(role);
    navigate(PROFILE_META[role].dashboard, { replace: true });
  };

  const isDark = theme === 'dark';
  const sectionBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #EDE8D8';
  const labelColor = isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af';
  const inactiveBg = isDark ? 'rgba(255,255,255,0.06)' : '#F9F5E8';
  const inactiveColor = isDark ? 'rgba(255,255,255,0.5)' : '#6b7280';
  const inactiveBorder = isDark ? 'rgba(255,255,255,0.12)' : '#E8D8A8';

  return (
    <div style={{ padding: '10px 12px', borderTop: sectionBorder }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: labelColor, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
        Changer de profil
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {allRoles.map(role => {
          const meta = PROFILE_META[role];
          if (!meta) return null;
          const Icon = meta.icon;
          const isActive = role === activeRole;
          return (
            <button
              key={role}
              title={isActive ? `Profil actif : ${meta.label}` : `Basculer vers ${meta.label}`}
              onClick={() => handleSwitch(role)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '4px 10px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: isActive ? 700 : 500,
                cursor: isActive ? 'default' : 'pointer',
                border: `1.5px solid ${isActive ? meta.border : inactiveBorder}`,
                background: isActive ? meta.color : inactiveBg,
                color: isActive ? '#fff' : inactiveColor,
                transition: 'all 0.15s',
                outline: 'none',
              }}
              onMouseEnter={e => {
                if (!isActive) {
                  e.currentTarget.style.background = `${meta.color}22`;
                  e.currentTarget.style.color = meta.color;
                  e.currentTarget.style.borderColor = meta.border;
                }
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  e.currentTarget.style.background = inactiveBg;
                  e.currentTarget.style.color = inactiveColor;
                  e.currentTarget.style.borderColor = inactiveBorder;
                }
              }}
            >
              <Icon size={11} />
              {meta.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
